import React, { useMemo } from 'react';

import { scaleShapePath } from '../avatar/scaleShapePath';

type Props = {
  /** Ссылка на уже скачанную анимацию: в вебе это `blob:`-адрес из `mediaCache`. */
  uri: string;
  size: number;
  /** Та же маска, что у обычного аватара: путь в системе координат 0–100. */
  pathD: string;
};

/**
 * Живая (анимированная) аватарка в вебе.
 *
 * <p>Раньше здесь стояла заглушка с объяснением «браузер сам анимирует `<image>` внутри SVG».
 * Для Chrome это правда, для WebKit — нет: в Safari, в том числе на iPhone, `<image>` в SVG
 * показывает только первый кадр GIF-а. Со стороны это выглядело ровно так, как сказал владелец
 * 2026-09-05: «аватарка двигается, только когда увеличишь». Увеличенный снимок открывается
 * обычным `<img>` ({@link UserPhotoViewer}) и потому живёт, а бейдж рядом — нет.
 *
 * <p><b>Поэтому слой рисуется настоящим `<img>`.</b> DOM-элемент напрямую — тот же приём, что и
 * в {@link DateTimeField.web}: `react-native-web` не даёт ни `objectFit`, ни `clip-path`, а
 * нужны оба. Форму держит `clip-path: path(…)` с путём, пересчитанным в пиксели
 * ({@link scaleShapePath}): маска берётся из того же каталога форм, что и у неподвижного
 * аватара, поэтому щит остаётся щитом, а не превращается в квадрат.
 *
 * <p>Если путь пересчитать не удалось, слоя нет вовсе: квадратная аватарка поверх круглой
 * читалась бы поломкой, а без слоя остаётся прежний неподвижный кадр — некрасиво, но целостно.
 */
export function AnimatedAvatarLayer({ uri, size, pathD }: Props): React.ReactElement | null {
  const clipPath = useMemo(() => {
    const scaled = scaleShapePath(pathD, size / 100);
    return scaled ? `path('${scaled}')` : null;
  }, [pathD, size]);

  if (!clipPath) return null;

  return (
    <div
      style={{
        width: size,
        height: size,
        clipPath,
        WebkitClipPath: clipPath,
        overflow: 'hidden',
      }}>
      <img
        src={uri}
        alt=""
        draggable={false}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </div>
  );
}
